/**
 * Sealed portals (plan 23 row 54).
 *
 * A teleporter platform can be authored `sealed: true` (flat on the level-JSON
 * platform, or inside `teleporterConfig`). A sealed portal is still placed and
 * still names where it led, but it leads nowhere: the client draws it dim and
 * cold with a "<place> — sealed" label and no key hint, never treats it as an
 * interaction target, and the server refuses the trip
 * (`world-transfer/PortalTransferPolicy` → `portal_sealed`).
 *
 * Unsealing is a data change: drop the flag from the level JSON. The First
 * Hearth's portal back into The Waking Vault is the first seal; production's
 * world experience also blocks that edge (`WorldExperience`
 * `blockedNaturalTransfers`), so reopening the vault on prod removes both.
 */

export interface PortalSealInput {
  sealed?: boolean;
  unsealQuest?: string;
  teleporterConfig?: { sealed?: boolean; unsealQuest?: string } | null;
}

/**
 * The quest that opens a sealed portal, from level data (`unsealQuest`, flat on
 * the platform or inside `teleporterConfig`). Brackenmoor's `tp_coil_exit`
 * names `the_hollow_keeper`; a seal without one (the Waking Vault) never opens.
 */
export function portalUnsealQuest(portal: PortalSealInput): string | null {
  const quest = portal.teleporterConfig?.unsealQuest ?? portal.unsealQuest;
  return typeof quest === 'string' && quest.length > 0 ? quest : null;
}

/**
 * `doneQuests` are the quests that count as done for unsealing: completed ones
 * plus active ones whose objectives are met, so the Coil exit opens as soon as
 * the Keeper falls. Without it (map graphs, prompts) a sealed portal reads as
 * sealed. `bypass` (GM, peaceful tourist) opens a portal that has an unseal quest.
 */
export function isPortalSealed(
  portal: PortalSealInput,
  doneQuests?: Iterable<string> | null,
  bypass: boolean = false,
): boolean {
  const sealedInData = portal.sealed === true || portal.teleporterConfig?.sealed === true;
  if (!sealedInData) return false;
  const unsealQuest = portalUnsealQuest(portal);
  if (!unsealQuest) return true;
  if (bypass) return false;
  for (const id of doneQuests ?? []) {
    if (id === unsealQuest) return false;
  }
  return true;
}

/** The world label a sealed portal shows instead of "[E / ↑] Enter …". */
export function sealedPortalLabel(destination: string | null): string {
  return destination ? `${destination} — sealed` : 'Sealed';
}
